import React, { useRef } from 'react';
import { Box } from '@landr/maestro';
import useDetectTargetIsInsideViewport from 'hooks/useDetectTargetIsInsideViewport/useDetectTargetIsInsideViewport';
import Slice from 'components/Slice/Slice';
import useQueryContentGroup from 'apollo/contentGroup/useQueryContentGroup';
import PreviewProvider from './contexts/PreviewContext/PreviewContext';
import { CarouselsProps, _CarouselsProps } from './types';
import CarouselContext from './contexts/CarouselContext';
import Tabs from './components/Tabs';
import Title from './components/Title';
import Byline from './components/Byline';

const _Carousels = React.forwardRef(
    (
        { isLoading, title, byline, contentLists }: _CarouselsProps,
        forwardedRef:
            | ((instance: HTMLDivElement | null) => void)
            | React.MutableRefObject<HTMLDivElement | null>
            | null,
    ) => {
        return (
            <Slice>
                <Box
                    data-component="Carousels"
                    ref={forwardedRef}
                    aria-live="polite"
                    aria-busy={isLoading}
                >
                    <Box mb="xl">
                        <Title content={title} isLoading={isLoading} />
                        <Byline content={byline} isLoading={isLoading} />
                    </Box>
                    <PreviewProvider contentGroup={title}>
                        <Tabs
                            contentLists={contentLists}
                            isLoading={isLoading}
                        />
                    </PreviewProvider>
                </Box>
            </Slice>
        );
    },
);

export const CarouselsSkeleton = () => {
    return <_Carousels isLoading={true} />;
};

const Carousels = ({ guid, title, byline }: CarouselsProps) => {
    const ref = useRef<HTMLDivElement | null>(null);
    const isInsideViewport = useDetectTargetIsInsideViewport({ ref });
    const { isLoading, contentLists } = useQueryContentGroup({
        guid,
        skip: !isInsideViewport,
    });

    return (
        <CarouselContext.Provider
            value={{
                carouselGuid: guid ? (guid as string) : '',
                carouselName: title ? (title as string) : '',
            }}
        >
            <_Carousels
                ref={ref}
                isLoading={isLoading}
                title={title}
                byline={byline}
                contentLists={contentLists}
            />
        </CarouselContext.Provider>
    );
};

export default Carousels;
